import { __assign, __awaiter, __generator } from "tslib";
import { ApiClient } from '@inspace-org/client';
import React from 'react';
// ----------------------------------------------------------------------
var Types;
(function (Types) {
    Types["INITIAL"] = "INITIAL";
})(Types || (Types = {}));
var initialState = {
    isAuthenticated: false,
    isInitialized: false,
    device: null,
    settings: null,
    branding: null,
    companySettings: null,
};
function reducer(state, action) {
    if (action.type === Types.INITIAL) {
        return __assign(__assign({}, state), { isInitialized: true, device: action.payload.device || null, settings: action.payload.settings || null, branding: action.payload.branding || null, companySettings: action.payload.companySettings, isAuthenticated: action.payload.isAuthenticated });
    }
    return state;
}
export var DeviceAuthContext = React.createContext(null);
export var DeviceAuthContextProvider = function (_a) {
    var apiUrl = _a.apiUrl, credentials = _a.credentials, language = _a.language, children = _a.children;
    var _b = React.useReducer(reducer, initialState), state = _b[0], dispatch = _b[1];
    var apiClient = React.useMemo(function () { return new ApiClient({ baseUrl: apiUrl, prefix: '', credentials: credentials, language: language }); }, [apiUrl, credentials]);
    var initialize = React.useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var pathname, hash, _a, accessToken, refreshToken, isAuthenticated, device, data, exchangeToken, res, res, res, settings, companySettings, branding, err_1;
        var _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (!credentials) {
                        throw new Error('`credentials` prop must be provided to <DeviceAuthContextProvider />.');
                    }
                    if (!apiUrl) {
                        throw new Error('`apiUrl` prop must be provided to <DeviceAuthContextProvider />.');
                    }
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 16, , 17]);
                    pathname = window.location.pathname;
                    hash = '';
                    // we don't have access to router here, so we need to check the pathname
                    if (pathname.startsWith('/connect/')) {
                        hash = pathname.split('/').pop() || '';
                    }
                    return [4 /*yield*/, credentials.getTokens()];
                case 2:
                    _a = _c.sent(), accessToken = _a.accessToken, refreshToken = _a.refreshToken;
                    isAuthenticated = false;
                    device = null;
                    if (!hash) return [3 /*break*/, 6];
                    return [4 /*yield*/, apiClient.get("/api/v1/link/".concat(hash))];
                case 3:
                    data = (_c.sent()).data;
                    exchangeToken = data.token;
                    return [4 /*yield*/, apiClient.post('/api/v1/auth/exchange', { exchangeToken: exchangeToken })];
                case 4:
                    res = _c.sent();
                    return [4 /*yield*/, credentials.setTokens(res.accessToken, res.refreshToken)];
                case 5:
                    _c.sent();
                    device = res.device;
                    isAuthenticated = true;
                    return [3 /*break*/, 11];
                case 6:
                    if (!refreshToken) return [3 /*break*/, 9];
                    return [4 /*yield*/, apiClient.post('/api/v1/auth/update', { refreshToken: refreshToken })];
                case 7:
                    res = _c.sent();
                    return [4 /*yield*/, credentials.setTokens(res.accessToken, res.refreshToken)];
                case 8:
                    _c.sent();
                    device = res.device;
                    isAuthenticated = true;
                    return [3 /*break*/, 11];
                case 9:
                    if (!accessToken) return [3 /*break*/, 11];
                    return [4 /*yield*/, apiClient.get('/api/v1/auth/check')];
                case 10:
                    res = _c.sent();
                    device = res.device;
                    isAuthenticated = true;
                    _c.label = 11;
                case 11:
                    settings = null;
                    if (!device) return [3 /*break*/, 13];
                    return [4 /*yield*/, apiClient.get("/api/v2/devices/".concat(device.uuid, "/device-settings"))];
                case 12:
                    settings = _c.sent();
                    _c.label = 13;
                case 13:
                    companySettings = void 0;
                    branding = void 0;
                    if (!isAuthenticated) return [3 /*break*/, 15];
                    return [4 /*yield*/, Promise.all([
                            apiClient.get('/api/v1/company-branding'),
                            apiClient.get("/api/v1/company-settings"),
                        ])];
                case 14:
                    _b = _c.sent(), branding = _b[0], companySettings = _b[1];
                    _c.label = 15;
                case 15:
                    dispatch({ type: Types.INITIAL, payload: { isAuthenticated: isAuthenticated, settings: settings, device: device, branding: branding, companySettings: companySettings } });
                    return [3 /*break*/, 17];
                case 16:
                    err_1 = _c.sent();
                    console.error(err_1);
                    dispatch({ type: Types.INITIAL, payload: { isAuthenticated: false } });
                    return [3 /*break*/, 17];
                case 17: return [2 /*return*/];
            }
        });
    }); }, [apiClient]);
    React.useEffect(function () {
        initialize();
    }, [initialize]);
    var value = React.useMemo(function () { return ({
        device: state.device,
        settings: state.settings,
        branding: state.branding,
        companySettings: state.companySettings,
        isInitialized: state.isInitialized,
        isAuthenticated: state.isAuthenticated,
    }); }, [state.device, state.settings, state.branding, state.companySettings, state.isInitialized, state.isAuthenticated]);
    return React.createElement(DeviceAuthContext.Provider, { value: value }, children);
};
